/**
 * Trunnion AI - Tool Registry Manager
 * Tool CRUD interface for managing available tools.
 */

import { useState } from 'react';
import { Plus, Wrench, Trash2, Edit2, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToolList } from '@/hooks/queries/useTools';
import type { ToolDefinition, ClassificationLevel } from '@/types';

export function ToolRegistryManager() {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useToolList({ page, pageSize: 25 });

  const columns: Column<ToolDefinition>[] = [
    {
      key: 'name',
      header: 'Tool',
      sortable: true,
      render: (t) => (
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-trunnion-gold-500" />
          <div>
            <span className="text-white font-medium">{t.name}</span>
            <p className="text-xs text-trunnion-slate-400">{t.category}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'classification',
      header: 'Level',
      render: (t) => <ClassificationTag level={t.classification as ClassificationLevel} />,
    },
    {
      key: 'method',
      header: 'Method',
      render: (t) => (
        <Badge variant="default">{t.method}</Badge>
      ),
    },
    {
      key: 'params',
      header: 'Params',
      render: (t) => (
        <span className="text-trunnion-slate-300">{t.parameters.length}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => (
        <Badge variant={t.isActive ? 'success' : 'default'} dot>
          {t.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-white" title="Test">
            <TestTube className="h-3.5 w-3.5" />
          </button>
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-white" title="Edit">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-red-400" title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Tool Registry</h2>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Register Tool
        </Button>
      </div>

      <DataTable<ToolDefinition>
        columns={columns}
        data={data?.tools ?? []}
        keyExtractor={(t) => t.id}
        loading={isLoading}
        emptyMessage="No tools registered"
        page={page}
        pageSize={25}
        total={data?.total}
        onPageChange={setPage}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Register Tool"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Register</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Tool Name" placeholder="Enter tool name" />
          <Input label="Description" placeholder="Describe what this tool does" />
          <Select
            label="Category"
            options={[
              { value: 'data_retrieval', label: 'Data Retrieval' },
              { value: 'data_analysis', label: 'Data Analysis' },
              { value: 'communication', label: 'Communication' },
              { value: 'file_operations', label: 'File Operations' },
              { value: 'system_operations', label: 'System Operations' },
              { value: 'ai_ml', label: 'AI/ML' },
              { value: 'security', label: 'Security' },
              { value: 'integration', label: 'Integration' },
              { value: 'custom', label: 'Custom' },
            ]}
          />
          <Input label="Endpoint" placeholder="https://api.example.com/tool" />
          <Select
            label="Method"
            options={[
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
              { value: 'PUT', label: 'PUT' },
              { value: 'DELETE', label: 'DELETE' },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
}
